import * as THREE from 'three';
import {readFileBuffer} from "../file-system-loader";
import {DataProjectionCoordinate, SceneCoordinate} from '../projections';
import {Viewer} from '../main';

const multiplyRotations = (rotationA, rotationB) => {
  let matrixA = rotationA instanceof THREE.Euler
    ? new THREE.Matrix4().makeRotationFromEuler(rotationA)
    : rotationA;

  let matrixB = rotationB instanceof THREE.Euler
    ? new THREE.Matrix4().makeRotationFromEuler(rotationB)
    : rotationB;

  return new THREE.Matrix4().multiplyMatrices(matrixA, matrixB);
};

class ImagePosition {
  private _value;
  private image: PanoramicImage;

  constructor(image: PanoramicImage, position) {
    this.image = image;
    this._value = new THREE.Vector3(...position);
  }

  get offset() {
    let matrix = new THREE.Matrix4()
      .copy(this.image.matrix)
      .transpose();

    return new THREE.Vector3()
      .setFromMatrixPosition(matrix);
  }

  get height() {
    return this.image.height;
  }

  get uncorrectedEuler() {
    return this.image.rotation._euler;
  }

  get value() {
    let offset = new THREE.Vector3()
      .copy(this.offset)
      .applyEuler(this.image.rotation.eulerCSV);

    let position = new THREE.Vector3()
      .copy(this._value)
      .add(offset);

    return position;
  }

  get viewer() {
    return new DataProjectionCoordinate(this.value).toScene();
  }

  get marker() {
    const offset = new THREE.Vector3(0, 0, this.height);
    return this.viewer.sub(offset);
  }

  get aerial() {
    return new SceneCoordinate(this.value).toAerial();
  }
}

class ImageRotation {
  private array: [];
  public _euler: THREE.Euler;
  private image: PanoramicImage;

  constructor(image: PanoramicImage, rotation) {
    this.image = image;
    this.array = rotation;
    this._euler = new THREE.Euler(
      -1 * THREE.MathUtils.degToRad(rotation[0]),
      -1 * THREE.MathUtils.degToRad(rotation[1]),
      -1 * THREE.MathUtils.degToRad(rotation[2])
    );
  }

  get matrix() {
    return new THREE.Matrix4()
      .copy(this.image.matrix)
      .setPosition(0,0,0);
  }

  get adjustment() {
    const angles = this.image.adjustment;
    return new THREE.Euler(
      -1 * THREE.MathUtils.degToRad(angles[0]),
      -1 * THREE.MathUtils.degToRad(angles[1]),
      -1 * THREE.MathUtils.degToRad(angles[2])
    );
  }

  get euler() {
    let rotationWithTransform = multiplyRotations(this._euler, this.matrix);
    let rotationWithAdjustment = multiplyRotations(rotationWithTransform, this.adjustment);

    let euler = new THREE.Euler()
      .setFromRotationMatrix(rotationWithAdjustment);

    return euler;
  }

  get viewer() {
    return new THREE.Euler().copy(this.euler);
  }

  get eulerCSV() {
    return this._euler;
  }

  get eulerDegrees() {
    let degrees = new THREE.Euler();
    degrees.x = -1 * THREE.MathUtils.radToDeg(this.euler.x);
    degrees.y = -1 * THREE.MathUtils.radToDeg(this.euler.y);
    degrees.z = -1 * THREE.MathUtils.radToDeg(this.euler.z);
    return degrees;
  }

  get eulerCsvInvert() {
    let inverseQuaternion = new THREE.Quaternion().setFromEuler(this.eulerCSV).inverse();
    let eulerOriginalInverse = new THREE.Euler().setFromQuaternion(inverseQuaternion);
    return eulerOriginalInverse;
  }
}

class ImageFace {
  private data;
  private material;
  private name;
  private rotation;
  private index;
  private preview;
  private interpolation;
  private worker;

  constructor(material, name, rotation, index) {
    this.data = null;
    this.material = material;
    this.name = name;
    this.rotation = rotation;
    this.index = index;

    const workerURL = "workers/convert-panoramic.js";
    this.worker = new Worker(`${process.env.PUBLIC_URL}/${workerURL}`);
  }

  destroy() {
    this.worker.terminate();
  }

  /** @private */
  workerCallback(resolve, imageData) {
    let texture = new THREE.DataTexture(imageData.data, imageData.width,
      imageData.height, THREE.RGBAFormat );
    texture.needsUpdate = true;
    texture.flipY = true;
    texture.rotation = THREE.MathUtils.degToRad(this.rotation);
    texture.center = new THREE.Vector2(0.5, 0.5);

    this.material.map = texture;
    this.material.color = null;
    this.material.map.needsUpdate = true;
    this.material.visible = true;
    this.material.needsUpdate = true;
    resolve(true);
  }

  /** @private */
  async renderFace(preview) {
    this.preview = preview;

    const promise = new Promise((resolve, reject) => {
      const options = {
        // Available methods are nearest, bilinear, cubic and lanczos
        interpolation: this.preview ? "nearest" : "bilinear",
        preview: this.preview,
        data: this.data,
        face: this.name
      };

      this.worker.onmessage = event => {
        this.workerCallback(resolve, event.data);
      };

      this.worker.postMessage(options);
    });

    return promise;
  }
}

export class PanoramicImage {
  public id;
  public csv;
  public name;
  private path;
  private faces;
  private materials;
  public mesh;
  private geometry;
  public enabled;
  public position;
  public rotation;
  public height;
  public adjustment;
  private img;
  private ctx;
  private canvas;
  public cameraList: PanoramicList;
  public isRemoteFile;

  constructor(cameraList, data) {
    this.cameraList = cameraList;
    this.id = data.id;
    this.csv = data.csv;
    this.name = data.name;
    this.path = data.path;
    this.height = data.height;
    this.adjustment = data.adjustment;
    this.isRemoteFile = data.remote;

    this.position = new ImagePosition(this, data.position);
    this.rotation = new ImageRotation(this, data.rotation);

    this.reset();
  }

  get scene() {
    return this.cameraList.scene;
  }

  get camera() {
    return this.cameraList.camera;
  }

  get matrix() {
    return this.cameraList.matrix;
  }

  get type() {
    let extension = this.name.split(".").slice(-1)[0].toLowerCase();
    let imageTypes = {
      "jpg": "image/jpeg",
      "jpeg": "image/jpeg",
      "png": "image/png"
    };

    return imageTypes[extension] || null;
  }

  get viewer() {
    return this.cameraList.viewer;
  }

  reset() {
    this.faces = [];
    this.materials = [];
    this.mesh = null;
    this.geometry = null;
    this.enabled = false;
  }

  loadCamera() {
    if (this.enabled) {
      this.cameraList.setNormalCamera();
      return;
    }

    this.enabled = true;
    this.generateMesh();
    this.mesh.visible = false;

    this.grabImageFromPath()
      .then(() => {
        // Start loading preview for all faces
        const data = this.imageToData(true);
        return this.processFaces(data, true);
      })
      .then(() => {
        // Clear current camera, move to new position
        this.viewer.setCurrentCamera(this.id);
        this.cameraList.clearImages(this);
        this.cameraList.setNormalCamera();
        this.setCameraPosition();
        this.mesh.visible = true;

        setTimeout(() => {
          // Preview is finished, load the full data
          const data = this.imageToData(false);
          return this.processFaces(data, false);
        }, 200);
      })
      .catch(() => {
        this.reset();

        console.error(`Error loading image: ${this.path}`);
        this.viewer.showMessage(`Error loading image: ${this.name}`, {
          variant: "error"
        });

        if (this.cameraList.current) {
          // Move back to previous camera
          const previousID = this.cameraList.current.id;
          this.viewer.setCurrentCamera(previousID);
        } else {
          // Nothing to fall back to, so just go to orbit
          this.cameraList.setOrbitCamera();
        }
      });
  }

  destroy() {
    if (!this.enabled) {
      return;
    }

    if (this.mesh) {
      this.scene.remove(this.mesh);
    }

    this.faces.forEach(face => {
      face.destroy();
    });

    this.materials.forEach(material => {
      if (material.map) {
        material.map.dispose();
      }
      material.dispose();
    });

    if (this.geometry) {
      this.geometry.dispose();
    }

    this.reset();
  }

  /** @private */
  determineImageSize(preview) {
    let width;
    let minWidth = 2048;
    let maxWidth = 8192;

    if (preview) {
      width = minWidth;
    } else {
      width = this.img.width;
      width = Math.min(width, maxWidth);
      width = Math.max(width, minWidth);
    }

    let height = width / 2.0;
    return {width, height};
  }

  /** @private */
  imageToData(preview) {
    // Draw image on canvas, using forced size for good quality
    const {width, height} = this.determineImageSize(preview);
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx.drawImage(this.img, 0, 0, width, height);
    const data = this.ctx.getImageData(0, 0, width, height);
    return data;
  }

  /** @private */
  grabImageFromPath() {
    return new Promise((resolve, reject) => {
      this.img = new Image();

      this.img.addEventListener('load', () => {
        this.canvas = document.createElement("canvas");
        this.ctx = this.canvas.getContext("2d");
        resolve(true);
      });

      let onSuccess = buffer => {
        // Load image source from buffer
        let arrayBufferView = new Uint8Array(buffer);
        let blob = new Blob([arrayBufferView], {type: this.type});
        this.img.src = URL.createObjectURL(blob);
      };

      let onError = () => {
        reject();
      };

      readFileBuffer(this.path, this.isRemoteFile)
        .then(onSuccess)
        .catch(onError);
    });
  }

  /** @private */
  setCameraPosition() {
    this.camera.position.copy(this.position.viewer);
    this.mesh.position.copy(this.position.viewer);
    this.mesh.rotation.copy(this.rotation.viewer);
  }

  /** @private */
  generateMesh() {
    let directions = [
      {direction: 'front', rotation: -90},
      {direction: 'back', rotation: 90},
      {direction: 'left', rotation: 0},
      {direction: 'right', rotation: 180},
      {direction: 'bottom', rotation: -90},
      {direction: 'top', rotation: -90}
    ];

    directions.forEach(({direction, rotation}, index) => {
      let material = new THREE.MeshBasicMaterial({
        side: THREE.DoubleSide,
        visible: false
      });
      this.materials.push(material);

      let face = new ImageFace(material, direction, rotation, index);
      this.faces.push(face);
    });

    this.geometry = new THREE.BoxBufferGeometry(10, 10, 10);
    this.mesh = new THREE.Mesh(this.geometry, this.materials);
    this.mesh.geometry.scale(1, 1, -1);
    this.scene.add(this.mesh);
  }

  /** @private */
  processFaces(data, preview) {
    return new Promise((resolve, reject) => {
      let promises = [];
      this.faces.forEach(face => {
        face.data = data;
        let result = face.renderFace(preview);
        promises.push(result);
      });

      Promise.all(promises).then(() => {
        resolve(true);
      });
    });
  }

  applyTempRotation(angles) {
    const angleEuler = new THREE.Euler(
      -1 * THREE.MathUtils.degToRad(angles[0]),
      -1 * THREE.MathUtils.degToRad(angles[1]),
      -1 * THREE.MathUtils.degToRad(angles[2])
    );

    const tempRotation = multiplyRotations(
      this.rotation.eulerCSV, angleEuler);
    const euler = new THREE.Euler()
      .setFromRotationMatrix(tempRotation);

    this.mesh.rotation.copy(euler);
  }
}

export class PanoramicList {
  public viewer: Viewer;
  public scene: any;
  public props;
  public cameras: PanoramicImage[];
  public matrix = new THREE.Matrix4();
  public adjustments = {};
  public aligned = false;

  constructor(viewer) {
    this.viewer = viewer;
    this.cameras = [];

    this.initScene();
    this.initEvents();
  }

  get count() {
    return this.cameras.length;
  }

  get camera() {
    return this.viewer.camera;
  }

  get controls() {
    return this.viewer.controls;
  }

  get pointclouds() {
    return this.viewer.pointclouds;
  }

  get minimap() {
    return this.viewer.minimap;
  }

  get markers() {
    return this.viewer.markers;
  }

  get current() {
    let cameras = this.cameras.filter(camera => camera.enabled);
    return cameras.length > 0 ? cameras[0] : null;
  }

  get currentIndex() {
    return this.cameras.indexOf(this.current);
  }

  get cameraLoaded() {
    return this.current !== null;
  }

  get centerLonLat() {
    let position = new THREE.Vector3();
    this.cameras.forEach(camera => {
      position.add(camera.position.value);
    });

    position.divideScalar(this.cameras.length);
    return new DataProjectionCoordinate(position).toLonLat();
  }

  get isEmpty() {
    return this.cameras.length === 0;
  }

  initEvents() {
    return;
  }

  initScene() {
    const scene = new THREE.Scene();
    const softlight = new THREE.AmbientLight(0xFFFFFF);
    scene.add(softlight);
    this.scene = scene;
  }

  onKeyDown(event) {
    if (event.key === "ArrowUp") {
      this.incrementCamera(1);
    } else if (event.key === "ArrowDown") {
      this.incrementCamera(-1);
    }
  }

  /** @private */
  setNormalCamera() {
    let controls = this.controls;
    controls.setOrbitState(false);
  }

  /** @private */
  setOrbitCamera() {
    let controls = this.controls;
    controls.setOrbitState(true);
  }

  /** @private */
  addNewCamera(data, currentCamera) {
    let cameraID = data.id;
    if (this.getByID(cameraID)) {
      // Camera already exists
      return false;
    }

    let camera = new PanoramicImage(this, data);
    this.cameras.push(camera);

    // Load camera if needed
    if (currentCamera === cameraID) {
      camera.loadCamera();
    }
  }

  deleteCamera(identifier) {
    const cameras = this.cameras.filter(camera => camera.id === identifier);
    if (cameras.length === 0) {
      return;
    }

    const camera = cameras[0];
    const index = this.cameras.indexOf(camera);
    // console.log(`Remove camera [id = ${camera.id}]`);
    camera.destroy();
    this.minimap.removeCameraMarker(camera);
    this.markers.removeCameraMarker(camera);
    this.cameras.splice(index, 1);
  }

  getByID(cameraID) {
    let cameras = this.cameras.filter(camera => camera.id === cameraID);
    return cameras.length === 1 ? cameras[0] : null;
  }

  loadImage(cameraID) {
    let camera = this.getByID(cameraID);
    if (!camera) {
      return;
    }

    //console.log(`Load camera: ${camera.name}`);
    camera.loadCamera();
  }

  clearImages(currentCamera: PanoramicImage) {
    /** Destroy all images except the current one */
    this.cameras.forEach(camera => {
      if (camera.id === currentCamera.id) {
        return;
      }
      camera.destroy();
    });
  }

  setMeshVisible(state) {
    this.cameras.forEach(camera => {
      if (state && !camera.enabled) {
        return;
      }

      if (camera.mesh) {
        camera.mesh.visible = state;
      }
    });
  }

  incrementCamera(increment) {
    if (!this.current) {
      return;
    }

    let cameraIndex = this.currentIndex;
    let nextIndex = cameraIndex + increment;
    nextIndex = (nextIndex + this.count) % this.count ;
    let nextCamera = this.cameras[nextIndex];
    this.loadImage(nextCamera.id);
  }

  setCameraHeights(height) {
    this.cameras.forEach(camera => {
      camera.height = height;
    });
    this.markers.recalculateAllMarkers();
  }

  setCameraAdjustments(adjustments) {
    this.cameras.forEach(camera => {
      const adjustment = camera.id in adjustments
        ? adjustments[camera.id]
        : [0, 0, 0];
      camera.adjustment = adjustment;
    });
  }

  setCameraMatrix(elements) {
    const newMatrix = new THREE.Matrix4().fromArray(elements);
    if (!this.matrix.equals(newMatrix)) {
      this.matrix = newMatrix;
      this.applyMatrixToCameras();
      this.aligned = true;
    }
  }

  /** Reset to identity matrix */
  resetCameraMatrix() {
    this.matrix = new THREE.Matrix4();
    this.applyMatrixToCameras();
    this.aligned = false;
  }

  applyMatrixToCameras() {
    this.minimap.recalculateAllMarkers();
    this.markers.recalculateAllMarkers();

    if (this.current) {
      this.setNormalCamera();
      this.current.setCameraPosition();
    }
  }

  update() {
    return;
  }
}
